/**
 * Qué documentos tiene una historia clínica.
 *
 * ESPEJO de backend/src/services/pdf/documentRegistry.js (availableKinds) y de
 * los constructores de bloques que decide cada null: si aquí se ofrece algo que
 * allá devuelve null, el usuario pulsa y recibe un 404. Por eso las condiciones
 * replican línea a línea las del backend. Si se cambia una, se cambia
 * la otra; la tabla utils/documentAvailability.cases.json la ejecutan ambos
 * lados y rompe la prueba del que se quede atrás.
 */

// Mismo orden que DOC_KINDS del backend: es el que ve el usuario en el menú.
export const DOC_KINDS = ['historia', 'medicamentos', 'examenes', 'incapacidad'];

// Tope de días de incapacidad temporal (backend: sickLeaveDates.MAX_DAYS).
const MAX_SICK_LEAVE_DAYS = 180;

// Equivale al safe() del backend: un texto en blanco cuenta como ausente.
const hasText = (value) => value !== null && value !== undefined && String(value).trim() !== '';

// prescriptionDocument: al menos un medicamento con nombre.
function hasMedications(record) {
  const meds = record?.conduct?.medications;
  return Array.isArray(meds) && meds.some((m) => hasText(m?.name));
}

// labOrderDocument: habilitada y al menos un examen con nombre.
function hasRequestedTests(record) {
  const tests = record?.requestedTests;
  return !!tests?.enabled && Array.isArray(tests.items) && tests.items.some((i) => hasText(i?.name));
}

// normalizeSickLeave: habilitada, con inicio, días ENTEROS entre 1 y 180 y una
// fecha de fin (la guardada, o calculable a partir de un inicio legible). Los
// días inválidos anulan el certificado aunque haya fecha de fin guardada.
function hasSickLeave(record) {
  const leave = record?.sickLeave;
  if (!leave?.enabled) return false;
  const days = Number(leave.days);
  if (!leave.startDate || !Number.isInteger(days) || days < 1 || days > MAX_SICK_LEAVE_DAYS) return false;
  if (leave.endDate) return true;
  return !Number.isNaN(new Date(leave.startDate).getTime());
}

const DOCUMENTS = [
  { kind: 'historia',     label: 'Historia clínica',           available: () => true },
  { kind: 'medicamentos', label: 'Fórmula médica',             available: hasMedications },
  { kind: 'examenes',     label: 'Orden de exámenes',          available: hasRequestedTests },
  { kind: 'incapacidad',  label: 'Certificado de incapacidad', available: hasSickLeave },
];

/** Documentos que ofrece el menú para esta historia, en el orden de DOC_KINDS. */
export const availableDocuments = (record) => DOCUMENTS.filter((doc) => doc.available(record));

/** Rótulo del documento (el mismo del menú de descarga); undefined si no existe. */
export const documentLabel = (kind) => DOCUMENTS.find((doc) => doc.kind === kind)?.label;

/** Lista cerrada: includes sobre un arreglo no resuelve prototipos (`__proto__`). */
export const isValidDocKind = (kind) => DOC_KINDS.includes(kind);
